import React, { useEffect } from "react";
import dynamic from "next/dynamic";

import { Action, Category } from "constants/events";
import { Store } from "custom-types/StoreLocator";
import { SVGProps } from "custom-types/SVGProps";
import { useEventTracker } from "hooks/useEventTracker";

import StarRating from "components/botanic/StarRating";
import Image from "components/Image";
import OfferLabel from "components/OfferLabel";

const ClearIcon = dynamic<SVGProps>(() => import("components/Icons/x.svg"));

const StorefrontIcon = dynamic<SVGProps>(
  () => import("components/Icons/storefront.svg"),
);

const Spotlight: React.FC<{
  onDismissed: () => void;
  store: Store;
}> = ({ onDismissed, store }) => {
  const { publishEvent } = useEventTracker();
  useEffect(() => {
    // Map Spotlight impressions and clicks and custom map marker/platinum listing clicks are critical company metrics
    publishEvent({
      action: Action.impression,
      category: Category.mapSpotlight,
      dispensaryId: store.id,
      isKeyMetric: true,
      label: store.name,
      merchandisingCampaignId: Number(store.merchandisingCampaignId),
    });
  }, []);

  const handleClick = () => {
    // Map Spotlight impressions and clicks and custom map marker/platinum listing clicks are critical company metrics
    publishEvent({
      action: Action.click,
      category: Category.mapSpotlight,
      dispensaryId: store.id,
      isKeyMetric: true,
      label: store.name,
      merchandisingCampaignId: Number(store.merchandisingCampaignId),
    });
  };

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onDismissed();

    // Map Spotlight impressions and clicks and custom map marker/platinum listing clicks are critical company metrics
    publishEvent({
      action: Action.click,
      category: Category.mapSpotlight,
      label: "close",
    });
  };

  return (
    <div
      className="bg-white shadow-low flex rounded m-4 md:m-0 relative p-md md:w-[224px]"
      data-testid="spotlight-store"
    >
      <button
        className="absolute left-3 top-3 bg-default opacity-60 rounded-full p-0.5 -mt-1 -ml-1 z-10"
        onClick={handleClose}
        title={`Click to close this ad`}
      >
        <ClearIcon className="text-white min-w-[20px]" width="20" height="20" />
      </button>
      <a
        data-testid="spotlight-link"
        className="w-full flex flex-wrap justify-between md:justify-start md:block"
        href={store.path}
        onClick={handleClick}
      >
        <div className="mx-auto h-[60px] w-[60px] md:h-[120px] md:w-[120px]">
          {store.logoUrl ? (
            <Image
              src={store.logoUrl}
              ratio="square"
              sizes={[60, null, 120, null, null, 120]}
              alt={`Spotlight: ${store.name}`}
              disableLazyLoad={true}
              preload={true}
            />
          ) : (
            <div className="p-md">
              <StorefrontIcon
                width="100%"
                height="100%"
                className="text-grey"
                aria-label={`Default logo for ${store.name}`}
              />
            </div>
          )}
        </div>
        <div className="flex flex-wrap w-3/4 md:w-full md:pt-md">
          <div className="grid w-5/6">
            <div className="text-xs underline truncate max-w-[95%] font-bold">
              {store.name}
            </div>
            {store.reviewCount > 0 && (
              <StarRating
                rating={store.reviewRating}
                ratingCount={store.reviewCount}
                starWidth={13}
              />
            )}
            {store.fulfillmentDetails && (
              <div className="flex-shrink-0 text-xs">
                {store.fulfillmentDetails}
              </div>
            )}
          </div>

          <div className="w-1/6 flex flex-1 justify-end items-start">
            <span className="text-xs px-xs bg-leafly-white text-grey rounded">
              ad
            </span>
          </div>

          {store.configurations.dealEnabled && (
            <div className="w-5/6 md:w-full pt-1" data-testid="spotlight-deal">
              {store.configurations.offerEnabled ? (
                <OfferLabel label={store.dealTitle} />
              ) : (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div
                      className="bg-notification rounded text-white font-bold mr-sm text-xs"
                      style={{ padding: "2px 4px" }}
                    >
                      {store.dealLabel}
                    </div>
                  </div>
                  <span
                    className="font-bold text-xs text-notification truncate-lines truncate-2-lines"
                    style={{ paddingTop: "2px" }}
                  >
                    {store.dealTitle}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </a>
    </div>
  );
};

export default Spotlight;
